import {
	useState, useEffect, useRef, useMemo, useCallback,
} from 'react';
import {
	getCurrentDateString,
} from '../../utils/dateUtils';
import {
	getEffectiveVCLResolution,
	toggleVCLResolution,
	getPersistableData,
	stableStringify,
} from '../../utils/vclUtils';
import {
	useVCLSlideData,
} from './useVCLSlideData';
import {
	useVCLProviderData,
} from './useVCLProviderData';
import {
	useVCLDurationCalculation,
} from './useVCLDurationCalculation';

const saveRefused = (message) => {
	const error = new Error(message);
	error.isSaveRefused = true;
	return error;
};

/**
 * Master hook for Visual Content Loop state management
 * Consolidates all state, operations, and business logic
 */
export const useVisualContentLoop = (visualContentLoopProps, isInFolderSchedulingEnabled = false) => {
	// Core state
	const [ loopDate, setLoopDate, ] = useState(getCurrentDateString());
	const [ campaigns, setCampaigns, ] = useState([]);
	const [ backfillContent, setBackfillContent, ] = useState([]);
	const [ isBackfillCollapsed, setIsBackfillCollapsed, ] = useState(false);
	const [ resolution, setResolution, ] = useState('');

	// Track original state for change detection
	const [ originalCampaigns, setOriginalCampaigns, ] = useState([]);
	const [ originalBackfillContent, setOriginalBackfillContent, ] = useState([]);
	const [ originalResolution, setOriginalResolution, ] = useState('');

	// Modal state
	const [ showAddContentModal, setShowAddContentModal, ] = useState(false);
	const [ addContentType, setAddContentType, ] = useState('content');
	const [ isLoadingSlideDecks, setIsLoadingSlideDecks, ] = useState(false);
	const [ availableSlideDecks, setAvailableSlideDecks, ] = useState([]);
	const [ availablePublishedFeeds, setAvailablePublishedFeeds, ] = useState([]);

	// Loading state
	const [ isSaving, setIsSaving, ] = useState(false);
	const [ isRefreshing, setIsRefreshing, ] = useState(false);

	const prevIdRef = useRef(visualContentLoopProps?.id);
	const hasLoadedInitialRef = useRef(false);

	const [ isBaselineLoaded, setIsBaselineLoaded, ] = useState(false);

	const campaignsRef = useRef(campaigns);
	const backfillContentRef = useRef(backfillContent);

	// Bumped synchronously wherever state is replaced outside the load effect — the consumer
	// setters, discard, save, refresh — never from an effect, so an in-flight load can tell
	// whether what it was built from is still on screen.
	const editSeqRef = useRef(0);

	// Monotonic generation of authoritative installs — VCL switch, completed Refresh, completed
	// Save. Awaited work captures it and drops its result if it moved. An id check cannot do
	// this: A→B→A restores the same id and resets editSeqRef.
	const stateGenRef = useRef(0);

	// Re-runs the load effect when a result had to be abandoned as stale.
	const [ reloadNonce, setReloadNonce, ] = useState(0);

	const isSavingRef = useRef(false);
	const isRefreshingRef = useRef(false);

	// Initialize hooks
	const {
		transformSlide,
		transformApiSlides,
		getOriginalSlidesForProvider,
	} = useVCLSlideData(loopDate, isInFolderSchedulingEnabled);

	const {
		fetchFreshVCLData,
		getInitialProviders,
		addProviderToVCL,
		saveProviders,
		fetchAvailableSlideDecks,
		fetchAvailablePublishedFeeds,
		getProviderType,
	} = useVCLProviderData(visualContentLoopProps?.id, transformSlide, transformApiSlides, visualContentLoopProps?.dynamic_durations, visualContentLoopProps?.account?.uuid);

	const {
		getProviderDuration,
		getTotalSlots,
		getTotalDurationFormatted,
	} = useVCLDurationCalculation(loopDate);

	// Function to detect if there are changes to save
	const hasChanges = useMemo(() => {
		// Deep compare campaigns and backfillContent with their original states (persistable fields only)
		const campaignsChanged = stableStringify(getPersistableData(campaigns)) !== stableStringify(getPersistableData(originalCampaigns));
		const backfillChanged = stableStringify(getPersistableData(backfillContent)) !== stableStringify(getPersistableData(originalBackfillContent));
		const resolutionChanged = resolution !== originalResolution;

		return campaignsChanged || backfillChanged || resolutionChanged;
	}, [ campaigns, originalCampaigns, backfillContent, originalBackfillContent, resolution, originalResolution, ]);

	const canSave = hasChanges && isBaselineLoaded;

	useEffect(() => {
		campaignsRef.current = campaigns;
		backfillContentRef.current = backfillContent;
	}, [ campaigns, backfillContent, ]);

	const editCampaigns = useCallback((next) => {
		editSeqRef.current += 1;
		setCampaigns(next);
	}, []);

	const editBackfillContent = useCallback((next) => {
		editSeqRef.current += 1;
		setBackfillContent(next);
	}, []);

	// The picker previews a FUTURE loop state, built from today's decks, so a past date
	// would render a loop that never played. Clamped here rather than in the JSX handler so
	// every caller is covered — `min` on the input only greys out the calendar UI, it does
	// not stop a typed or pasted value.
	const setLoopDateClamped = useCallback((next) => {
		const today = getCurrentDateString();
		setLoopDate(!next || next < today ? today : next);
	}, []);

	// Computed values
	const totalSlots = useMemo(() => {
		return getTotalSlots(campaigns);
	}, [ campaigns, getTotalSlots, ]);

	const totalDuration = useMemo(() => {
		return getTotalDurationFormatted(campaigns, backfillContent);
	}, [ campaigns, backfillContent, getTotalDurationFormatted, ]);

	// Load initial providers when component mounts or props change
	useEffect(() => {
		if (visualContentLoopProps?.id !== prevIdRef.current) {
			prevIdRef.current = visualContentLoopProps?.id;
			hasLoadedInitialRef.current = false;

			campaignsRef.current = [];
			backfillContentRef.current = [];
			editSeqRef.current = 0;
			stateGenRef.current += 1;

			setCampaigns([]);
			setBackfillContent([]);
			setOriginalCampaigns([]);
			setOriginalBackfillContent([]);
			setIsBaselineLoaded(false);

			const today = getCurrentDateString();
			if (loopDate !== today) {
				setLoopDate(today);
				return;
			}
		}

		let didCancel = false;
		const abortController = new AbortController();

		const loadProviders = async () => {
			const hasLiveState = hasLoadedInitialRef.current || editSeqRef.current > 0;
			const editSeqAtStart = editSeqRef.current;
			const stateGenAtStart = stateGenRef.current;
			// A loop that was never read cannot be told apart from a genuinely empty one
			// unless props actually carried a provider array.
			const propsProviders = visualContentLoopProps?.providers;
			const propsAreReadable = Array.isArray(propsProviders);

			try {
				const sourceProviders = hasLiveState
					? [ ...campaignsRef.current, ...backfillContentRef.current, ]
					: (propsAreReadable ? propsProviders : []);

				const providersData = await getInitialProviders(
					sourceProviders,
					false,
					abortController.signal
				);

				if (didCancel) {
					return;
				}

				if (!hasLiveState) {
					if (stateGenRef.current !== stateGenAtStart) {
						return;
					}

					const serverIds = new Set([
						...providersData.campaigns,
						...providersData.backfillContent,
					].map((provider) => {
						return provider.id;
					}));
					const userAdditions = (prev) => {
						return prev.filter((provider) => {
							return !serverIds.has(provider.id);
						});
					};

					setCampaigns((prev) => {
						return [ ...providersData.campaigns, ...userAdditions(prev), ];
					});
					setBackfillContent((prev) => {
						return [ ...providersData.backfillContent, ...userAdditions(prev), ];
					});

					// Baseline is the server's providers only — never the merge, or the user's
					// addition would look as though it had already been saved.
					if (propsAreReadable) {
						hasLoadedInitialRef.current = true;
						setOriginalCampaigns(JSON.parse(JSON.stringify(providersData.campaigns)));
						setOriginalBackfillContent(JSON.parse(JSON.stringify(providersData.backfillContent)));
						setIsBaselineLoaded(true);
					}
					return;
				}

				if (editSeqRef.current !== editSeqAtStart) {
					setReloadNonce((n) => {
						return n + 1;
					});
					return;
				}

				setCampaigns(providersData.campaigns);
				setBackfillContent(providersData.backfillContent);
			} catch (error) {
				if (didCancel) {
					return;
				}
				// If request was aborted, don't update state
				if (error.name === 'AbortError') {
					return;
				}
				if (!hasLiveState && editSeqRef.current === editSeqAtStart) {
					setCampaigns([]);
					setBackfillContent([]);
					setOriginalCampaigns([]);
					setOriginalBackfillContent([]);
				}
			}
		};

		loadProviders();

		return () => {
			didCancel = true;
			abortController.abort();
		};
	}, [ getInitialProviders, loopDate, reloadNonce, visualContentLoopProps?.id, visualContentLoopProps?.providers, ]);

	// Initialize resolution from props only — not on loopDate changes, which would
	// revert a just-saved toggle to the stale pre-save value from props.
	// Effective resolution: explicit VCL value > backend-computed effective > account default > vertical
	useEffect(() => {
		const effectiveResolution = visualContentLoopProps?.resolution
			|| visualContentLoopProps?.effective_resolution
			|| getEffectiveVCLResolution(null, visualContentLoopProps?.account?.visual_content_loop_providers);
		setResolution(effectiveResolution);
		setOriginalResolution(effectiveResolution);
	}, [ visualContentLoopProps, ]);

	// Modal functions
	const openAddContentModal = (type) => {
		setAddContentType(type);
		setShowAddContentModal(true);
		// Fetch available slide decks when modal opens
		setIsLoadingSlideDecks(true);
		fetchAvailableSlideDecks(campaigns, backfillContent).then((availableDecks) => {
			setAvailableSlideDecks(availableDecks);
			setIsLoadingSlideDecks(false);
		}).catch(() => {
			setAvailableSlideDecks([]);
			setIsLoadingSlideDecks(false);
		});
		fetchAvailablePublishedFeeds(campaigns, backfillContent).then((availableFeeds) => {
			setAvailablePublishedFeeds(availableFeeds);
		}).catch(() => {
			setAvailablePublishedFeeds([]);
		});
	};

	const closeAddContentModal = () => {
		setShowAddContentModal(false);
		// Clear available slide decks when modal closes
		setAvailableSlideDecks([]);
		setAvailablePublishedFeeds([]);
	};

	// Wrapper function for adding providers using the hook
	const handleAddProvider = async (providerData) => {
		// Published feeds arrive from the Add Content modal without slides (the
		// availability route is a lightweight list), so hydrate them through the
		// feed-authorized deck route first — otherwise the new provider renders as
		// pure BACKFILLED until a slot change or reload refetches it (DEV-11955 QA).
		// Close first so a slow feed fetch can't be double-clicked into two adds
		closeAddContentModal();

		let hydratedProviderData = providerData;
		if (providerData?.type === 'published_feed' && !Array.isArray(providerData.slides)) {
			const slides = await getOriginalSlidesForProvider(
				providerData.id,
				'published_feed',
				visualContentLoopProps?.account?.uuid
			);
			hydratedProviderData = {
				...providerData,
				slides,
			};
		}

		const newProvider = addProviderToVCL(hydratedProviderData, addContentType, campaigns, backfillContent);

		if (addContentType === 'content') {
			editCampaigns((prev) => {
				return [ ...prev, newProvider, ];
			});
		} else {
			editBackfillContent((prev) => {
				return [ ...prev, newProvider, ];
			});
		}
	};

	// Refresh function
	const handleRefresh = async () => {
		if (isSavingRef.current) {
			return;
		}

		if (hasChanges) {
			const proceed = typeof window !== 'undefined' && typeof window.confirm === 'function'
				&& window.confirm('Refreshing discards your unsaved changes to this loop. Continue?');
			if (!proceed) {
				return;
			}
		}

		isRefreshingRef.current = true;
		setIsRefreshing(true);
		const startTime = Date.now();
		const stateGenAtStart = stateGenRef.current;
		const editSeqAtStart = editSeqRef.current;

		try {
			const newProviders = await getInitialProviders([], true);

			if (stateGenRef.current !== stateGenAtStart || editSeqRef.current !== editSeqAtStart) {
				return;
			}

			editSeqRef.current += 1;
			stateGenRef.current += 1;

			setCampaigns(newProviders.campaigns);
			setBackfillContent(newProviders.backfillContent);

			// Update original state after refresh
			setOriginalCampaigns(JSON.parse(JSON.stringify(newProviders.campaigns)));
			setOriginalBackfillContent(JSON.parse(JSON.stringify(newProviders.backfillContent)));

			hasLoadedInitialRef.current = true;
			setIsBaselineLoaded(true);
		} catch (error) {
			// Handle refresh error silently or show notification
			if (error.name !== 'AbortError') {
				// Could show error notification here
			}
		} finally {
			// Ensure at least 1 second of loading state for visual feedback
			const elapsedTime = Date.now() - startTime;
			const minRefreshTime = 1000; // 1 second

			if (elapsedTime < minRefreshTime) {
				await new Promise((resolve) => {
					return setTimeout(resolve, minRefreshTime - elapsedTime);
				});
			}

			isRefreshingRef.current = false;
			setIsRefreshing(false);
		}
	};

	// Save function
	const handleSave = async () => {
		if (!hasChanges) {
			// No changes detected, return early without saving
			return;
		}

		// saveProviders PUTs the whole provider list, so saving without a trustworthy
		// baseline would silently delete every provider the failed load never showed.
		// This throws rather than returning quietly: the Save button is already disabled,
		// so the only way in is the navigate-away prompt, and that must not report success.
		if (!isBaselineLoaded) {
			throw saveRefused('This loop could not be loaded. Refresh it before saving.');
		}

		if (isRefreshingRef.current) {
			throw saveRefused('This loop is refreshing. Wait for the refresh to finish before saving.');
		}

		isSavingRef.current = true;
		setIsSaving(true);
		const startTime = Date.now();
		const stateGenAtStart = stateGenRef.current;
		const editSeqAtStart = editSeqRef.current;

		const savedCampaigns = campaigns;
		const savedBackfillContent = backfillContent;
		const savedResolution = resolution;

		try {
			// Only persist the resolution when the user actually toggled it, so
			// untouched VCLs keep inheriting the account-level default
			await saveProviders(savedCampaigns, savedBackfillContent, savedResolution !== originalResolution ? savedResolution : undefined);

			if (stateGenRef.current !== stateGenAtStart) {
				return;
			}

			// Clear deleted items — and unpublished-feed tombstones the save just
			// dropped — from state after successful save
			const updatedCampaigns = savedCampaigns.filter((campaign) => {
				return !campaign.isDeleted && !campaign.unavailable;
			});
			const updatedBackfillContent = savedBackfillContent.filter((item) => {
				return !item.isDeleted && !item.unavailable;
			});

			if (editSeqRef.current === editSeqAtStart) {
				editSeqRef.current += 1;

				setCampaigns(updatedCampaigns);
				setBackfillContent(updatedBackfillContent);
			}

			stateGenRef.current += 1;

			// Update original state to reflect the saved state
			setOriginalCampaigns(JSON.parse(JSON.stringify(updatedCampaigns)));
			setOriginalBackfillContent(JSON.parse(JSON.stringify(updatedBackfillContent)));
			setOriginalResolution(savedResolution);
		} catch (error) {
			// Handle save error
			if (error.name !== 'AbortError') {
				// Could show error notification here
				throw error;
			}
		} finally {
			// Ensure at least 1 second of loading state for visual feedback
			const elapsedTime = Date.now() - startTime;
			const minSaveTime = 1000; // 1 second

			if (elapsedTime < minSaveTime) {
				await new Promise((resolve) => {
					return setTimeout(resolve, minSaveTime - elapsedTime);
				});
			}

			isSavingRef.current = false;
			setIsSaving(false);
		}
	};

	// Discard changes function
	const handleDiscardChanges = () => {
		// Nothing to discard, or `original*` is not a real baseline — discarding onto an
		// empty baseline would clear a loop that only looks empty because it failed to load.
		if (!canSave) {
			return;
		}

		editSeqRef.current += 1;

		// Reset to original state by deep cloning the original arrays
		setCampaigns(JSON.parse(JSON.stringify(originalCampaigns)));
		setBackfillContent(JSON.parse(JSON.stringify(originalBackfillContent)));
		setResolution(originalResolution);
	};

	// Toggle the loop resolution between vertical and horizontal (persisted on Save)
	const handleToggleResolution = () => {
		setResolution((prev) => {
			return toggleVCLResolution(prev);
		});
	};


	return {
		// State
		loopDate,
		setLoopDate: setLoopDateClamped,
		campaigns,
		setCampaigns: editCampaigns,
		backfillContent,
		setBackfillContent: editBackfillContent,
		isBackfillCollapsed,
		setIsBackfillCollapsed,
		resolution,

		// Modal state
		showAddContentModal,
		addContentType,
		isLoadingSlideDecks,
		availableSlideDecks,
		availablePublishedFeeds,

		// Loading state
		isSaving,
		isRefreshing,

		// Computed values
		totalSlots,
		totalDuration,
		hasChanges,
		// Whether those changes may be persisted — false until the loop has been hydrated
		// from the server at least once (see isBaselineLoaded)
		canSave,
		isBaselineLoaded,

		// Functions
		getProviderDuration,
		getOriginalSlidesForProvider,
		transformSlide,
		openAddContentModal,
		closeAddContentModal,
		handleAddProvider,
		handleRefresh,
		handleSave,
		handleDiscardChanges,
		handleToggleResolution,

		// Provider utility
		getProviderType,

	};
};